/**
 * useCapturaHydrateUnmasked — hidratación COMPLETA (incluye PII descifrada)
 * del formulario de Captura.
 *
 * Diferencia con `useCapturaHydrate`:
 *   - `useCapturaHydrate` consulta Supabase directo (RLS) y solo devuelve
 *     campos no-PII + `titular_masked`. Útil cuando NO hay MFA reciente
 *     o se prefiere mostrar datos masked.
 *   - `useCapturaHydrateUnmasked` consulta el BFF (`GET /v1/captura/:id`)
 *     que descifra `titular_v2`, `telefono_v2`, etc. con la llave AES en
 *     el Worker. Requiere `aal2` en el JWT (TTL 30 min) — si no, el BFF
 *     responde 403 `mfa_required` y el hook deja `data=null` con un
 *     `error` específico para que el caller monte `MfaUnlockModal`.
 *
 * Política de seguridad:
 *   - Solo se llama on-demand desde la página de Captura (un click del
 *     técnico al abrir la parcela), nunca como background fetch en
 *     listados — eso multiplicaría el número de eventos
 *     `propietario.unmask_for_hydration` en LFPDPPP audit.
 *   - El error de MFA es discriminable (`status === 403` con
 *     `error: 'mfa_required'`) para que CapturaPage abra el modal sin
 *     confundirlo con errores genéricos.
 *
 * Cache: NO usamos `useCachedFetch` aquí. La PII descifrada NO debe
 * persistirse en sessionStorage (donde `useCachedFetch` la dejaría) —
 * el TTL de la PII en memoria del componente es suficiente. Si el
 * técnico recarga la página, vuelve a pasar por el modal MFA.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { bffFetch, BffError } from '../helpers/bff';

export interface CapturaHydrateUnmaskedPropietario {
  id: string;
  /** Cleartext canónico UPPERCASE. Nunca null si la descifra fue OK. */
  titular: string | null;
  telefono: string | null;
  email: string | null;
  curp: string | null;
  rfc: string | null;
  ine_numero: string | null;
  direccion: string | null;
  clabe: string | null;
  tipo: 'ejidatario' | 'comunero' | 'posesionario' | 'propietario' | 'avecindado' | null;
  folio_derechos: string | null;
  folio_tierras: string | null;
  clave_catastral: string | null;
  titular_masked: string | null;
  pii_version: 1 | 2;
}

export interface CapturaHydrateUnmaskedGestion {
  estado: string | null;
  firmado: boolean | null;
  riesgo: 'bajo' | 'medio' | 'alto' | null;
  plazo_anos: number | null;
  monto_renta_ha: number | null;
  /** JSON crudo (string). El consumidor parsea con su propio helper para
   *  reusar la lógica de `useGestionNotas`. */
  notas: string | null;
  enganche_pct: number | null;
}

export interface CapturaHydrateUnmaskedIrregularidad {
  subtipo: string;
  gravedad: 'leve' | 'media' | 'alta' | 'bloqueante';
  descripcion: string | null;
  resuelta: boolean | null;
}

export interface CapturaHydrateUnmaskedData {
  parcela_existe: boolean;
  propietario: CapturaHydrateUnmaskedPropietario | null;
  gestion: CapturaHydrateUnmaskedGestion | null;
  irregularidades: CapturaHydrateUnmaskedIrregularidad[];
}

/**
 * Estado del hook. `mfaRequired=true` indica que el caller debe abrir
 * `MfaUnlockModal` y luego llamar `refetch()` cuando el modal devuelva
 * `onSuccess`.
 */
export interface CapturaHydrateUnmaskedState {
  data: CapturaHydrateUnmaskedData | null;
  loading: boolean;
  error: Error | null;
  mfaRequired: boolean;
  refetch: () => Promise<void>;
}

/**
 * `enabled=false` desactiva el fetch (no hace nada hasta que se vuelva
 * true). Útil cuando el caller quiere disparar la hidratación solo
 * después de que el user confirme "sí, prefiero los datos del servidor"
 * — política `no pisar trabajo en curso` definida en CapturaPage.
 */
export function useCapturaHydrateUnmasked(
  parcelaPublicId: string | undefined,
  enabled: boolean = true,
): CapturaHydrateUnmaskedState {
  const [data, setData] = useState<CapturaHydrateUnmaskedData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const [mfaRequired, setMfaRequired] = useState<boolean>(false);
  /** Aborta requests previos al cambiar de parcela. Evita race conditions
   *  donde la respuesta de la parcela A llega después que la de B. */
  const abortRef = useRef<AbortController | null>(null);

  const doFetch = useCallback(async (): Promise<void> => {
    if (!parcelaPublicId) {
      setData(null);
      setError(null);
      setMfaRequired(false);
      return;
    }
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    // Limpiar `data` antes de fetch nuevo — sin esto, durante la transición
    // entre parcelas el effect de aplicación en CapturaPage puede correr
    // con la `data` stale (de la parcela anterior) y aplicarla al form
    // nuevo. La pantalla muestra "loading" en ese intermedio, lo cual es
    // deseable: mejor un blank que datos del propietario equivocado.
    setData(null);
    setLoading(true);
    setError(null);
    setMfaRequired(false);
    try {
      const json = await bffFetch<CapturaHydrateUnmaskedData>(
        `/v1/captura/${encodeURIComponent(parcelaPublicId)}`,
        { signal: ac.signal },
      );
      if (ac.signal.aborted) return;
      setData(json);
    } catch (e) {
      if (ac.signal.aborted) return;
      // 403 mfa_required → distinguible para que CapturaPage abra el modal.
      if (e instanceof BffError && e.status === 403) {
        const detail = e.detail as { error?: string } | undefined;
        if (detail?.error === 'mfa_required') {
          setMfaRequired(true);
          setData(null);
          return;
        }
        if (detail?.error === 'pii_forbidden') {
          // Toggle pii_access=false: sin acceso a PII. Caída limpia a
          // enmascarado — sin error visible ni modal MFA.
          setData(null);
          return;
        }
      }
      setError(e instanceof Error ? e : new Error(String(e)));
      setData(null);
    } finally {
      if (!ac.signal.aborted) setLoading(false);
    }
  }, [parcelaPublicId]);

  // Auto-fetch cuando cambia parcelaId o enabled. Si enabled pasa de false
  // a true, esto dispara la primera carga.
  useEffect(() => {
    if (!enabled) return;
    void doFetch();
    return () => { abortRef.current?.abort(); };
  }, [doFetch, enabled]);

  return { data, loading, error, mfaRequired, refetch: doFetch };
}
